import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { BindingEngine } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import * as moment from "moment";
import * as Enumerable from "linq";
import { BindingSignaler } from "aurelia-templating-resources";
import { DataType, QueryFilter } from "@rightship-tech/common-ui/models/queries.models";
import { ClientMetaModel, ClientMetaPageModel, ConditionalGroup, FormAttachment, FormAttachmentAttributes, FormMetaConfig, FormMetaItem, FormMetaPage, GetFormAttachmentListAttributeRequest, GetOnboardingRequestResponse, OnboardingConfigModel, OnboardingRequestModel, OnboardingRequestRuleModel } from "../../../models/form.models";
import { BaseOnboardingSection } from "./sections/base-onboarding-section";
import { RightshipOnboardingApi } from "../../../apis/rightship-onboarding.api";
import { RightShipTransformHtmlHelper } from "../../../helpers/rightship-transform-html-helper";
import { PLATFORM } from "aurelia-framework";

@autoinject()
@transient()
export class FormViewer
{
    public item: OnboardingRequestModel;
    protected currentPageNumber: number = 0;
    protected currentPage: ClientMetaPageModel;
    protected basicPages: ClientMetaPageModel[] = [];
    protected ifBindables: { [guid: string]: { isVisible: boolean, dependencies: string[], conditionalGroup: ConditionalGroup } } = {};
    protected readonly: boolean = true;
    public previousBasicConfigurations: ClientMetaModel[] = [];
    public basicConfigurations: ClientMetaModel[] = [];
    public onboardingRequestsPath: string;
    public pluginName: string;
    public sections: { [guid: string]: { currentViewModel: BaseOnboardingSection } } = {};
    public currentOnboardingRequest: OnboardingRequestModel;
    public currentOnboardingConfig: OnboardingConfigModel;
    public sectionsShowing: boolean;
    public formAttachmentAttributes: FormAttachmentAttributes[] = [];

    @bindable()
    public onboardingRequestId: number;

    @bindable()
    public sectionGrouping: {
        sectionTitle: string;
        properties: string[];
    }[] = [];

    constructor(protected bindingEngine: BindingEngine, private bindingSignaler: BindingSignaler, private configService: ConfigService, 
        private rightshipOnboardingApi: RightshipOnboardingApi) { }

    activate(data: { onboardingRequestId: number }) {
        this.onboardingRequestId = data.onboardingRequestId;
        this.onboardingRequestIdChanged();
    }

    public sectionViewModel(item: FormMetaItem) {
        if (!item.section) {
            return;
        }
        require(`./sections/${item.section}/${item.section}`);
        return PLATFORM.moduleName(`./sections/${item.section}/${item.section}`);
    }

    public getAttachmentSectionViewModel() {
        require(`./sections/attachments-section/attachments-section`);
        return PLATFORM.moduleName(`./sections/attachments-section/attachments-section`);
    }

    public sectionModel(item: FormMetaItem) {
        return {
            readonly: this.readonly,
            onboardingRequest: this.currentOnboardingRequest,
            guid: item.guid,
            formAttachmentAttributes: this.formAttachmentAttributes
        };
    }

    public createdDateFormatter = (item) => {
        return moment(item.createdDate).format('DD MMM YYYY HH:mm');
    }

    public areConditionsSatisfied(item: any, filters: QueryFilter[]): boolean {
        const groupedFilters = Enumerable.from(filters).groupBy(x => x.groupGuid);
        let response: boolean = true;
        groupedFilters.forEach(filterGroup => {
            let groupResponse: boolean = true;
            filterGroup.forEach(filter => {
                const operator = filter.filterOperator.friendlyName;
                const type = filter.filterProperty.type;
                if (filter.isAnd) {
                    groupResponse = groupResponse && this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue);
                }
                else {
                    groupResponse = groupResponse || this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue);
                }
            });
            response = response && groupResponse;
        });
        return response;
    }

    public refreshAnswers(configurations: ClientMetaModel[], pages: ClientMetaPageModel[], previousConfigurations: ClientMetaModel[]) {

        const tempPages: ClientMetaPageModel[] = [];
        let previousItems = [];

        this.ifBindables = {};

        configurations.forEach(configuration => {

            configuration.metaConfig = configuration.isMetaFormStyle && configuration.config ? JSON.parse(configuration.config) : configuration.config;
            if (configuration.isMetaFormStyle) {
                if (!(<FormMetaConfig>configuration.metaConfig).pages) {
                    (<FormMetaConfig>configuration.metaConfig).pages = [];
                }

                (<FormMetaConfig>configuration.metaConfig).pages.forEach((page) => {

                    page.items.forEach(item => {
                        if (item.conditionalGroupGuid && page.conditionalGroups[item.conditionalGroupGuid] && !this.ifBindables[item.conditionalGroupGuid]) {

                            this.ifBindables[item.conditionalGroupGuid] = {
                                isVisible: true,
                                conditionalGroup: page.conditionalGroups[item.conditionalGroupGuid],
                                dependencies: page.conditionalGroups[item.conditionalGroupGuid].filters.map(x => x.filterProperty.name)
                            }
                        }
                        const propsBinded = [];
                        const answerElement = RightShipTransformHtmlHelper.transformHtml(item.answerHtml, configuration.model, configuration.properties, (element: HTMLElement, propName: string, dataType: DataType) => {

                            if (previousConfigurations && previousConfigurations.length > 0) {
                                const index = configurations.indexOf(configuration);

                                const previousValue = previousConfigurations[index].model[propName];
                                const newValue = configuration.model[propName];

                                if (newValue != previousValue) {

                                    if ((dataType == DataType.DateTime || dataType == DataType.Date) && !!newValue && !!previousValue) {
                                        const newDate = this.castDate(newValue).toDateString();
                                        const previousDate = this.castDate(previousValue).toDateString();

                                        if (newDate != previousDate) {
                                            element.classList.add('changed');
                                        }
                                    }
                                    else {
                                        element.classList.add('changed');
                                    }
                                }
                            }

                            propsBinded.push(propName);
                        });
                        
                        item.isStaticContent = propsBinded.length == 0;
                        item.answer = answerElement;
                        item.rules = configuration.rules.filter(z => propsBinded.indexOf(z.property) > -1).map(x => this.getMessage(x));
                        item.isVisible = item.section != null || (item.answer && (!!item.answer.textContent || item.answer.getElementsByClassName('transformed-element').length > 0));

                    });
                    tempPages.push({
                        clientMetaModel: configuration,
                        page: page,
                        content: null,
                        rulesNumber: Enumerable.from(page.items).sum(c => c.rules.length),
                        isVisible: page.items.length > 0 && page.items.some(x => x.isVisible)
                    });
                });
            }

            const model = configuration.model;
            const keys = Object.keys(configuration.model);
            const ifKeys = Object.keys(this.ifBindables);
            keys.forEach(key => {
                if (configuration.model[key] === undefined) {
                    configuration.model[key] = null;
                }

                const ifBindablesOnKey = ifKeys.filter(ifKey => this.ifBindables[ifKey] && this.ifBindables[ifKey].dependencies.some(z => z == key));

                const onPropChanged = () => {
                    ifBindablesOnKey.forEach(ifKey => {
                        this.ifBindables[ifKey].isVisible = this.areConditionsSatisfied(model, this.ifBindables[ifKey].conditionalGroup.filters);
                    });
                }
                onPropChanged();
            });
        });

        if (previousConfigurations) {
            previousConfigurations.forEach(previousConfiguration => {

                previousConfiguration.metaConfig = previousConfiguration.isMetaFormStyle && previousConfiguration.config ? JSON.parse(previousConfiguration.config) : previousConfiguration.config;
                if (previousConfiguration.isMetaFormStyle) {
                    (<FormMetaConfig>previousConfiguration.metaConfig).pages.forEach((page) => {
                        page.items.forEach(item => {

                            const propsBinded = [];
                            const answerElement = RightShipTransformHtmlHelper.transformHtml(item.answerHtml, previousConfiguration.model, previousConfiguration.properties, (element: HTMLElement, propName: string, dataType: DataType) => {

                                const index = previousConfigurations.indexOf(previousConfiguration);

                                const previousValue = previousConfiguration.model[propName];
                                const newValue = configurations[index].model[propName];

                                if (newValue != previousValue) {

                                    if ((dataType == DataType.DateTime || dataType == DataType.Date) && !!newValue && !!previousValue) {
                                        const newDate = this.castDate(newValue).toDateString();
                                        const previousDate = this.castDate(previousValue).toDateString();

                                        if (newDate != previousDate) {
                                            element.classList.add('changed');
                                        }
                                    }
                                    else {
                                        element.classList.add('changed');
                                    }
                                }
                                propsBinded.push(propName);
                            });

                            item.answer = answerElement;
                            item.rules = previousConfiguration.rules.filter(z => propsBinded.indexOf(z.property) > -1).map(x => this.getMessage(x));
                        });

                        previousItems = previousItems.concat(page.items);
                    });
                }
            });
        }

        while (pages.length > 0) {
            pages.pop();
        }
        tempPages.filter(x => x.isVisible).forEach(x => {
            pages.push(x);
        });
        this.currentPageNumber = 1;
        this.currentPage = pages[this.currentPageNumber - 1];
    }

    getMessage(rule: OnboardingRequestRuleModel) {
        if (!rule.clientSideParameters || !rule.clientSideParameters.length) {
            return rule.message;
        }
        else {
            let i;
            for (i = 0; i < rule.clientSideParameters.length; i++) {

                const momentObj = moment.utc(rule.clientSideParameters[i]);

                if (momentObj.isValid()) {
                    return rule.message.replace("{" + i + "}", momentObj.local().format(this.configService.config.common.defaultDateFormat));
                }

                return rule.message.replace("{" + i + "}", rule.clientSideParameters[i]);
            }
        }
    }


    isEqual = (valueToTest, symbol, type, value) => {
        type = Number(type);
        if (type == DataType.Number || type == DataType.Double) {
            valueToTest = valueToTest != null ? Number(valueToTest) : null;
            value = value != null ? Number(value) : null;
        } else if (type == DataType.Lookup) {
            valueToTest = valueToTest && Array.isArray(valueToTest) ? valueToTest.map(x => x.toString()) : valueToTest;
            value = value ? value.toString() : value;
        }
        if (symbol == "=") {
            return this.getEquality(valueToTest, type, value);
        } else if (symbol == "<>") {
            return valueToTest != value;
        } else if (symbol == "<=") {
            return valueToTest && valueToTest <= value;
        } else if (symbol == ">=") {
            return valueToTest && valueToTest >= value;
        } else if (symbol == "<") {
            return valueToTest && valueToTest < value;
        } else if (symbol == ">") {
            return valueToTest && valueToTest > value;
        } else if (symbol == "ends with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == (valueToTest.length - 1);
        } else if (symbol == "starts with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == 0;
        } else if (symbol == "contains") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) > -1;
        }
    }

    getEquality = (valueToTest, type, value) => {
        if (type == DataType.Boolean) {
            if(valueToTest == null)
                return value == null;
            if(valueToTest == undefined)
                return value == undefined;
            if(value == null)
                return valueToTest == null;
            if(value == undefined)
                return valueToTest == undefined;

            return valueToTest == value;
        }
        return valueToTest == value;
    }

    getModel(str: string) {
        try {
            const obj = str ? JSON.parse(str) : {};

            if (obj && obj.dynamicValues) {
                Object.keys(obj.dynamicValues).forEach(key => {
                    obj[key] = obj.dynamicValues[key];
                });
            }

            return obj;
        } catch (e) {
            console.error(e);
            return {};
        }
    }

    getConfigurationIndex(clientMetaModel: ClientMetaModel, configs: ClientMetaModel[]) {
        return configs.indexOf(clientMetaModel);
    }

    showSectionsMobile() {
        this.sectionsShowing = !this.sectionsShowing;
    }

    selectSection(page: ClientMetaPageModel) {
        this.sectionsShowing = false;
        this.currentPage = page;
        this.currentPageNumber = this.basicPages.findIndex(c => c == this.currentPage) + 1;
    }
    
    isLoading: boolean = true;
    public onboardingRequestIdChanged() {
        this.isLoading = true;

        let onboardingRequestIds = [this.onboardingRequestId];

        if (!onboardingRequestIds) {
            this.isLoading = false;
            return;
        }

        const hasPrevious = onboardingRequestIds.length > 1;

        const currentOnboardingRequestId = onboardingRequestIds[0];
        const previousOnboardingRequestId = hasPrevious ? onboardingRequestIds[1] : 0;

        this.previousBasicConfigurations = null;
        this.basicConfigurations = null;

        const promises = [];
        promises.push(this.rightshipOnboardingApi.getOnboardingRequest(currentOnboardingRequestId));

        if (hasPrevious)
            promises.push(this.rightshipOnboardingApi.getOnboardingRequest(previousOnboardingRequestId))

        Promise.all(promises).then(async response => {

            const previousOnboardingRequestResponse: GetOnboardingRequestResponse = hasPrevious ? response[2] : null;
            const currentOnboardingRequestResponse: GetOnboardingRequestResponse = response[0];

            const previousModel = hasPrevious ? this.getModel(previousOnboardingRequestResponse.onboardingRequest.modelMeta) : null;
            const currentModel = this.getModel(currentOnboardingRequestResponse.onboardingRequest.modelMeta);

            this.onboardingRequestsPath = currentOnboardingRequestResponse.onboardingRequestsPath;
            this.pluginName = currentOnboardingRequestResponse.pluginName;

            this.currentOnboardingRequest = currentOnboardingRequestResponse.onboardingRequest;
            const oc = await this.rightshipOnboardingApi.getOnboardingConfig(this.currentOnboardingRequest.onboardingConfig.onboardingConfigId);
            this.currentOnboardingConfig = oc.onboardingConfig;

            this.previousBasicConfigurations = hasPrevious ? [
                {
                    isMetaFormStyle: previousOnboardingRequestResponse.onboardingRequest.onboardingConfig.isBasicMetaFormStyle,
                    config: previousOnboardingRequestResponse.onboardingRequest.onboardingConfig.basicMetaConfig,
                    model: previousModel,
                    properties: previousOnboardingRequestResponse.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: previousOnboardingRequestResponse.onboardingRequest.rules
                }
            ] : null;

            this.basicConfigurations = [
                {
                    isMetaFormStyle: currentOnboardingRequestResponse.onboardingRequest.onboardingConfig.isBasicMetaFormStyle,
                    config: currentOnboardingRequestResponse.onboardingRequest.onboardingConfig.basicMetaConfig,
                    model: currentModel,
                    properties: currentOnboardingRequestResponse.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: currentOnboardingRequestResponse.onboardingRequest.rules
                }
            ];

            const formAttachments: GetFormAttachmentListAttributeRequest = {
                currentOnboardingRequestAttachments: currentOnboardingRequestResponse.onboardingRequest.uploadedDocuments.map<FormAttachment>(x => ({ onboardingRequestDocumentId: x.onboardingRequestDocumentId, groupingGuid: x.groupingGuid, documentName: x.documentName, documentPath: x.documentPath})),
                previousOnboardingRequestAttachments: !hasPrevious ? [] : previousOnboardingRequestResponse.onboardingRequest.uploadedDocuments.map<FormAttachment>(x => ({ onboardingRequestDocumentId: x.onboardingRequestDocumentId, groupingGuid: x.groupingGuid, documentName: x.documentName, documentPath: x.documentPath}))
            }

            this.refreshAnswers(this.basicConfigurations, this.basicPages, this.previousBasicConfigurations);

            try {
                //Get form attachment attributes
                const attachmentAttributes = await this.rightshipOnboardingApi.getFormAttachmentAttributes(formAttachments)
                
                this.formAttachmentAttributes = [...attachmentAttributes.formAttachmentAttributes];

                this.bindingSignaler.signal('form-attachment-attributes-changed');
            } catch (e) {
                console.error(e)
            }
            
            this.isLoading = false;
        });
    }

    get anySection() {
        return this.basicPages && this.basicPages.filter(item => item.page && item.page.title || item.clientMetaModel.fallbackTitle).length > 0;
    }

    public getPageTitle(page: FormMetaPage, clientMetaModel: ClientMetaModel) {
        return page.title ? page.title :
                        (clientMetaModel.doNotUseFallback ? '' : clientMetaModel.fallbackTitle)
    }

    private castDate(value: Date | string | moment.Moment): Date {
        if (value && (moment.isMoment(value))) {
            return (<moment.Moment>value).toDate();
        }
        return typeof value === "string" ? new Date(value) : <Date>value;
    }
}